import type { admin } from '@repletoryreach/sdk-api';
import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';

type Broadcast = admin.AdminBroadcast;

/** The status codes `GET /v1/admin/broadcasts` emits. */
export type BroadcastState =
  | 'draft'
  | 'scheduled'
  | 'running'
  | 'paused'
  | 'completed'
  | 'cancelled'
  | 'failed';

const STATES: BroadcastState[] = ['draft', 'scheduled', 'running', 'paused', 'completed', 'cancelled', 'failed'];

/**
 * The job's state, as the broadcasts service records it.
 *
 * This used to be guessed from timestamps, which could express only "not
 * started", "started" and "finished": a cancelled job and a paused one both
 * read as pending forever, and a clean send with three bounced recipients was
 * ranked below a total failure. The service now emits a `status` code and it
 * is the source of truth. Failure counts and `finished_at` are shown beside
 * the state rather than replacing it.
 *
 * `status` is optional on the wire for compatibility, so the timestamp
 * reading survives as a fallback for a deployment that has not been updated.
 * It cannot invent `paused` or `cancelled`; nothing can, from timestamps.
 */
export function stateOf(r: Broadcast): BroadcastState {
  const code = r.status?.toLowerCase();
  if (code && (STATES as string[]).includes(code)) return code as BroadcastState;
  if (r.finished_at) return 'completed';
  if (r.started_at) return 'running';
  if (r.scheduled_at) return 'scheduled';
  return 'draft';
}

export type BroadcastChannel = 'whatsapp' | 'sms' | 'email';

export const ALL_CHANNELS: BroadcastChannel[] = ['whatsapp', 'sms', 'email'];

export const CHANNEL_LABEL_KEYS: Record<BroadcastChannel, string> = {
  whatsapp: 'broadcasts.channel.whatsapp',
  sms: 'broadcasts.channel.sms',
  email: 'broadcasts.channel.email'
};

/** The channel it went out on; a row from before channels existed is WhatsApp. */
export function channelOf(r: Broadcast): BroadcastChannel {
  const c = r.channel?.toLowerCase();
  return c === 'sms' || c === 'email' ? c : 'whatsapp';
}

/**
 * What a person calls the broadcast (4 October 2026): the email subject, the SMS text or the template, as the
 * service names it. An SMS or email broadcast has no template, so the template code alone left an empty title.
 * Never empty: the template code, then the channel, stand in.
 */
export function broadcastTitle(r: Broadcast, t: (key: string) => string): string {
  const name = (r.name ?? '').replace(/\s+/g, ' ').trim();
  if (name) return name.length > 60 ? `${name.slice(0, 59).trimEnd()}…` : name;
  if (r.template_code) return r.template_code;
  return t(CHANNEL_LABEL_KEYS[channelOf(r)]);
}

/**
 * Recipients no longer queued: sent (delivered and read included), failed or left out. A finished job is done
 * however its last recipients ended, so its bar is full even when they failed.
 */
export function doneOf(r: Broadcast): number {
  if (r.finished_at) return r.recipients_total;
  return Math.min(r.recipients_total, r.recipients_sent + r.recipients_failed + r.recipients_filtered);
}

/**
 * Grey for a job that is not moving, blue for one in flight, amber for one
 * held, green for one done and red for one that failed.
 */
export const STATE_TONE: Record<BroadcastState, StatusTone> = {
  draft: 'inactive',
  scheduled: 'inactive',
  cancelled: 'inactive',
  running: 'pending',
  paused: 'warning',
  completed: 'healthy',
  failed: 'down'
};

export const STATE_LABEL_KEYS: Record<BroadcastState, string> = {
  draft: 'broadcasts.status.draft',
  scheduled: 'broadcasts.status.scheduled',
  running: 'broadcasts.status.running',
  paused: 'broadcasts.status.paused',
  completed: 'broadcasts.status.completed',
  cancelled: 'broadcasts.status.cancelled',
  failed: 'broadcasts.status.failed'
};

/** Every state, for the filter drawer, in lifecycle order. */
export const ALL_STATES = STATES;
